import { dispatch } from 'lib/store'
import apiClient from 'lib/api-client'
import { buildAttributesTree } from 'lib/search/build-attributes-tree'
import { getCommonQueryFilters, isDemoLoading } from 'selectors/search/filters'
import { getAttributeValues } from 'selectors/search/attributes'
import {
  SET_ATTRIBUTES,
  SET_ATTRIBUTES_TREE,
  SET_FETCHING_ATTRIBUTES,
  SET_ATTRIBUTE_VALUES,
  SET_FETCHING_ATTRIBUTE_VALUES,
  SET_SELECTED_ATTRIBUTE,
} from 'constants/action-types'
import type { Attribute, AttributeValueCount } from 'types/search/attributes'

const ATTRIBUTES_QUERY = `
  query {
    attributes {
      items {
        code
        name
        type
      }
    }
  }
`

interface AttributesResponse {
  data: {
    attributes: {
      items: Attribute[]
    }
  }
}

/**
 * Fetch attributes from GraphQL API and build tree structure
 * Errors are caught by global error handler
 */
export const fetchAttributes = async (): Promise<void> => {
  dispatch(SET_FETCHING_ATTRIBUTES, true)

  try {
    const response = await apiClient.post<AttributesResponse>('/graphql', {
      query: ATTRIBUTES_QUERY,
    })

    const attributes = response.data.data.attributes.items

    // Save raw attributes
    dispatch(SET_ATTRIBUTES, attributes)

    // Build and save tree structure
    const treeData = buildAttributesTree(attributes)
    dispatch(SET_ATTRIBUTES_TREE, treeData)

    // Auto-select 'message' attribute if present
    if (attributes.some((a) => a.code === 'message')) {
      dispatch(SET_SELECTED_ATTRIBUTE, 'message')
    }
  } finally {
    dispatch(SET_FETCHING_ATTRIBUTES, false)
  }
}

const ATTRIBUTE_VALUES_COUNT_QUERY = `
  query AttributeValuesCount($filter: AttributeValuesCountFilter) {
    attributeValuesCount(filter: $filter) {
      items {
        attributeValue
        count
      }
    }
  }
`

interface AttributeValuesCountResponse {
  data: {
    attributeValuesCount: {
      items: AttributeValueCount[]
    }
  }
}

interface AttributeValuesCountInput {
  attributeCode: string
  dateRange?: {
    from?: string | null
    to?: string | null
  }
  attributeValues?: Array<{
    attributeCode: string
    attributeValue: string
  }>
}

/**
 * Fetch attribute values count from GraphQL API
 * Uses common query filters from selector (memoized, same as chart and logs)
 * Excludes current attribute from filters for cross-filtering behavior
 * Errors are caught by global error handler
 */
export const fetchAttributeValues = async (
  attributeCode: string,
): Promise<void> => {
  dispatch(SET_FETCHING_ATTRIBUTE_VALUES, true)

  try {
    // Get filters from selector (memoized, consistent across all queries)
    const commonFilters = getCommonQueryFilters()

    // Build filter with spread (includes all common filters + any future additions)
    // Override attributeValues to exclude current attribute (cross-filtering)
    const filter: AttributeValuesCountInput = {
      ...commonFilters,
      attributeCode,
      attributeValues: commonFilters.attributeValues.filter(
        (av) => av.attributeCode !== attributeCode,
      ),
    }

    const response = await apiClient.post<AttributeValuesCountResponse>(
      '/graphql',
      {
        query: ATTRIBUTE_VALUES_COUNT_QUERY,
        variables: {
          filter,
        },
      },
    )

    const fetchedAttributeValues = response.data.data.attributeValuesCount.items

    // Save attribute values
    dispatch(SET_ATTRIBUTE_VALUES, fetchedAttributeValues)
  } finally {
    // In demo mode, keep loader active if no data - covers both
    // empty API response and error cases (retry will follow)
    if (!(isDemoLoading() && getAttributeValues().length === 0)) {
      dispatch(SET_FETCHING_ATTRIBUTE_VALUES, false)
    }
  }
}
